import { useState, useEffect, useRef } from 'react';
import { Tooltip } from '@/components/widgets/tooltip';
import { TooltipVariant, tooltip } from 'styled-system/recipes';
import { useTranslations } from 'next-intl';
import { addCommas, removeCommas } from '@/utils/odds-utils';
import { stakeSettingDefaultValue, stakeSettingMaxLength } from '@/hooks/query/use-account-settings-data';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import clsx from 'clsx';
import { mePanelStyle } from '../me-panel/me-panel.style';

interface StakeInputProps {
	inputKey?: number;
	inputValue: number;
	setInputValue: (input: number) => void;
	isFocus: boolean;
	setFocusKey: (inputKey: number) => void;
	isSelect: boolean;
	setSelectKey: (inputKey: number) => void;
	isDisabled?: boolean;
	tooltipVariantHorizontal?: TooltipVariant['horizontal'];
}

/** Profile Panel - Stake 設定輸入框 (for deafult stake & quick stake) */
export const StakeInput = ({
	inputKey = 0,
	inputValue,
	setInputValue,
	isFocus,
	setFocusKey,
	isSelect,
	setSelectKey,
	isDisabled,
	tooltipVariantHorizontal = 'right',
}: StakeInputProps) => {
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const [value, setValue] = useState('');
	const [isInputSelect, setIsInputSelect] = useState(false);
	const [message, setMessage] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);
	const hadEdited = useRef(false);
	const isDesktopMode = window.innerWidth >= 1024;
	const defaultStake = stakeSettingDefaultValue(view);

	useEffect(() => {
		setValue(inputValue.toString());
	}, [inputValue]);

	useEffect(() => {
		setIsInputSelect(false);
		setMessage('');
	}, [value]);

	useEffect(() => {
		setIsInputSelect(isSelect);
	}, [isSelect]);

	useEffect(() => {
		if (isFocus) {
			if (!isInputSelect) {
				setIsInputSelect(true); // 當 focus 要預設有全選效果
			}
			if (!hadEdited.current) {
				hadEdited.current = true;
			}
		} else {
			setIsInputSelect(false);
			if (hadEdited.current) {
				checkIsEmpty();
				inputRef.current?.blur();
			}
		}
	}, [isFocus]);

	useEffect(() => {
		if (isInputSelect) {
			setSelectKey(inputKey);
			inputRef.current?.select();
		} else {
			setSelectKey(-1);
		}
	}, [isInputSelect]);

	const toggleInput = () => {
		if (isDisabled || isDesktopMode) return;

		if (isFocus && !isInputSelect) {
			setIsInputSelect(true);
		} else {
			setIsInputSelect(false);
			setFocusKey(inputKey);
		}
	}

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setIsInputSelect(false);

		const input = e.target.value;
		let stake = parseInt(removeCommas(input).replace(/[^0-9]/g, '')) || 0;
		const stakeLength = stake.toString().length;
		if (stakeLength > stakeSettingMaxLength) {
			stake = 0;
		}
		setInputValue(stake);
	}

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const code = e.code;
		const isAlphaPressed = code.startsWith('Key');

		if (isAlphaPressed && !e.ctrlKey) {
			e.preventDefault();
			return;
		}

		if (code === 'Enter' || code === 'NumpadEnter') {
			setFocusKey(-1);
			return;
		}

		if (inputValue.toString().length >= stakeSettingMaxLength) {
			setInputValue(0);
		}
	}

	const onFocus = () => {
		setFocusKey(inputKey);
	};

	const preventCopyPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
	}

	const checkIsEmpty = () => {
		if (Number(value) > 0) return;
		view === ViewEnum.galaxy ? setInputValue(defaultStake) : setMessage(t('lbl_EmptyInput'));
	}

	return (
		<div className="stake-input stake-input--small"
			data-focus={isFocus}
			data-selected={isInputSelect}
			onClick={toggleInput}
		>
			<div className="stake-input__stake">
				<div className="stake-input__textfield">
					<div className={clsx('stake-input__input', mePanelStyle.stakeInputMobile)} data-show-placeholder={false}>
						{addCommas(value ?? '0')}
					</div>
					<input
						className={clsx('input', mePanelStyle.stakeInput)}
						type="input"
						ref={inputRef}
						value={addCommas(value ?? '0')}
						onFocus={onFocus}
						onChange={onChange}
						onKeyDown={onKeyDown}
						onPaste={preventCopyPaste}
						disabled={isDisabled}
					/>
				</div>
			</div>
			<Tooltip
				className={tooltip({ visual: 'brand', horizontal: tooltipVariantHorizontal, vertical: 'top', size: 'sm' })}
				message={message}
			/>
		</div>
	);
}